import { useCallback, useEffect, useRef, useState } from "react";

import { readHumanErrorMessage } from "@/shared/utils/apiError";

import { CardholderOnboardingService } from "../services/cardholderOnboarding.service";
import type { VerificationSyncResult } from "../types/cardholderOnboarding.types";

export type VerificationSyncPhase = "checking" | "review" | "applying" | "applied" | "error";

interface SyncCheck {
  result: VerificationSyncResult | null;
  /**
   * The approved sibling can be adopted: a dry run WITH `repoint_to_green` found no other identity holding
   * it.
   */
  canRepoint: boolean;
}

/** Compares one cardholder's verification with the provider's verdict, and brings ours in line. */
export function useVerificationSync(identityId: string, fallbackError: string) {
  const [phase, setPhase] = useState<VerificationSyncPhase>("checking");
  const [check, setCheck] = useState<SyncCheck>({ result: null, canRepoint: false });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const checkedRef = useRef(false);

  // The plain dry run never checks whether the sibling is taken; only a run with the flag does, so the
  // repoint button is offered from that second preview and never from the first.
  const requestCheck = useCallback(async (): Promise<SyncCheck> => {
    const plain = await CardholderOnboardingService.syncVerification({
      identity_ids: [identityId],
      dry_run: true,
    });
    const result = plain.results.find((row) => row.identity_id === identityId) ?? null;
    if (!result?.green_sibling_applicant_id) return { result, canRepoint: false };
    const withRepoint = await CardholderOnboardingService.syncVerification({
      identity_ids: [identityId],
      dry_run: true,
      repoint_to_green: true,
    });
    const preview = withRepoint.results.find((row) => row.identity_id === identityId);
    return { result, canRepoint: !!preview?.green_sibling_applicant_id && !preview.repoint_blocked };
  }, [identityId]);

  const showCheck = useCallback((next: SyncCheck) => {
    setCheck(next);
    setPhase("review");
  }, []);

  const showFailure = useCallback(
    (err: unknown) => {
      setErrorMessage(readHumanErrorMessage(err, fallbackError));
      setPhase("error");
    },
    [fallbackError],
  );

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    requestCheck().then(showCheck, showFailure);
  }, [requestCheck, showCheck, showFailure]);

  const recheck = useCallback(async () => {
    setPhase("checking");
    setErrorMessage(null);
    try {
      showCheck(await requestCheck());
    } catch (err) {
      showFailure(err);
    }
  }, [requestCheck, showCheck, showFailure]);

  const apply = useCallback(
    async (repointToGreen: boolean): Promise<boolean> => {
      setPhase("applying");
      setErrorMessage(null);
      try {
        const response = await CardholderOnboardingService.syncVerification({
          identity_ids: [identityId],
          dry_run: false,
          repoint_to_green: repointToGreen,
        });
        const result = response.results.find((row) => row.identity_id === identityId) ?? null;
        setCheck({ result, canRepoint: false });
        setPhase("applied");
        return result?.category === "updated" || result?.category === "repointed";
      } catch (err) {
        showFailure(err);
        return false;
      }
    },
    [identityId, showFailure],
  );

  return { phase, result: check.result, canRepoint: check.canRepoint, errorMessage, check: recheck, apply };
}
